import { Controller } from "@hotwired/stimulus"

/**
 * RalphSessionFormController - Handles Ralph session form interactions
 *
 * Features:
 * - "Generate Plan" button triggers AI plan generation
 * - Polls for generation status and updates the fix_plan textarea
 * - WebSocket connection for real-time progress updates
 */
export default class extends Controller {
  static targets = [
    "generateBtn",
    "fixPlan",
    "generationStatus",
    "generationMessage"
  ]

  static values = {
    sessionId: String,
    appProjectSlug: String
  }

  connect() {
    console.log("Ralph session form controller connected")
    this.generating = false
    this.pollingInterval = null
  }

  disconnect() {
    this.stopPolling()
  }

  async generatePlan(event) {
    event.preventDefault()

    if (this.generating) return

    // Get selected Linear issues from the selector controller
    const selectorElement = this.element.querySelector('[data-controller="linear-issue-selector"]')
    const selectedIdsInput = selectorElement?.querySelector('[data-linear-issue-selector-target="selectedIds"]')
    const descriptionInput = this.element.querySelector('[name="ralph_session[description]"]')

    // Gather all selected issue IDs
    const selectedInputs = this.element.querySelectorAll('input[name="ralph_session[linear_issue_ids][]"]')
    const linearIssueIds = Array.from(selectedInputs)
      .map(input => input.value)
      .filter(v => v && v.trim() !== '')

    const description = descriptionInput?.value?.trim()

    if (linearIssueIds.length === 0) {
      this.showError("Please select at least one Linear issue to generate a plan.")
      return
    }

    if (!description) {
      this.showError("Please enter a session description.")
      return
    }

    // For existing sessions, use the async generation flow
    if (this.sessionIdValue) {
      await this.triggerGeneration(linearIssueIds, description)
    } else {
      // For new sessions, use the preview endpoint for synchronous generation
      await this.generatePreviewPlan(linearIssueIds, description)
    }
  }

  async generatePreviewPlan(linearIssueIds, description) {
    this.generating = true
    this.showGenerationStatus("Generating implementation plan...")
    this.setButtonLoading(true)

    try {
      // Get the app project slug from the form action URL
      const formAction = this.element.action || window.location.pathname
      const appProjectSlug = this.extractAppProjectSlug(formAction)

      const response = await fetch(`/api/v2/app_projects/${appProjectSlug}/ralph_sessions/preview_plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        body: JSON.stringify({
          linear_issue_ids: linearIssueIds,
          description: description
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Update the fix_plan textarea with the generated plan
        if (data.data.fix_plan && this.hasFixPlanTarget) {
          this.fixPlanTarget.value = data.data.fix_plan
        }

        // Update the ralph_prompt textarea if it exists
        const ralphPromptInput = this.element.querySelector('[name="ralph_session[ralph_prompt]"]')
        if (data.data.ralph_prompt && ralphPromptInput) {
          ralphPromptInput.value = data.data.ralph_prompt
        }

        this.hideGenerationStatus()
        this.showSuccess(`Plan generated successfully! Analyzed ${data.data.issues_analyzed} issues.`)
      } else {
        this.showError(data.error || "Failed to generate plan")
      }
    } catch (error) {
      console.error("Error generating plan preview:", error)
      this.showError("Network error: " + error.message)
    } finally {
      this.generating = false
      this.setButtonLoading(false)
    }
  }

  extractAppProjectSlug(url) {
    // Extract app_project slug from URL like /app_projects/ralph-test/ralph_sessions/new
    const match = url.match(/app_projects\/([^\/]+)/)
    return match ? match[1] : ''
  }

  async triggerGeneration(linearIssueIds, description) {
    this.generating = true
    this.showGenerationStatus("Starting plan generation...")
    this.setButtonLoading(true)

    try {
      const response = await fetch(this.generatePlanUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        body: JSON.stringify({
          linear_issue_ids: linearIssueIds,
          description: description
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Start polling for status
        this.startPolling()
      } else {
        this.showError(data.error || "Failed to start plan generation")
        this.generating = false
        this.setButtonLoading(false)
      }
    } catch (error) {
      console.error("Error triggering plan generation:", error)
      this.showError("Network error: " + error.message)
      this.generating = false
      this.setButtonLoading(false)
    }
  }

  startPolling() {
    this.pollingInterval = setInterval(() => this.pollStatus(), 2000)
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  async pollStatus() {
    try {
      const response = await fetch(this.planningStatusUrl, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        }
      })

      const data = await response.json()

      if (response.ok && data.success && data.data) {
        const status = data.data.planning_status
        const progress = data.data.planning_progress

        this.showGenerationStatus(progress?.message || `Status: ${status}`)

        if (status === 'completed') {
          this.stopPolling()
          this.generating = false
          this.setButtonLoading(false)
          this.hideGenerationStatus()

          // Update the fix_plan textarea with the generated plan
          if (data.data.fix_plan && this.hasFixPlanTarget) {
            this.fixPlanTarget.value = data.data.fix_plan
            this.showSuccess("Plan generated successfully!")
          }
        } else if (status === 'failed') {
          this.stopPolling()
          this.generating = false
          this.setButtonLoading(false)
          this.showError(data.data.planning_error || "Plan generation failed")
        }
      }
    } catch (error) {
      console.error("Error polling status:", error)
    }
  }

  get generatePlanUrl() {
    return `/api/v2/app_projects/${this.appProjectSlugValue}/ralph_sessions/${this.sessionIdValue}/generate_plan`
  }

  get planningStatusUrl() {
    return `/api/v2/app_projects/${this.appProjectSlugValue}/ralph_sessions/${this.sessionIdValue}/planning_status`
  }

  showGenerationStatus(message) {
    if (this.hasGenerationStatusTarget) {
      this.generationStatusTarget.classList.remove('hidden')
    }
    if (this.hasGenerationMessageTarget) {
      this.generationMessageTarget.textContent = message
    }
  }

  hideGenerationStatus() {
    if (this.hasGenerationStatusTarget) {
      this.generationStatusTarget.classList.add('hidden')
    }
  }

  showError(message) {
    // Create a temporary alert
    const alert = document.createElement('div')
    alert.className = 'alert alert-error mb-4'
    alert.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>${message}</span>
      <button class="btn btn-sm btn-ghost" onclick="this.parentElement.remove()">×</button>
    `
    this.element.insertBefore(alert, this.element.firstChild)

    // Auto-remove after 5 seconds
    setTimeout(() => alert.remove(), 5000)
  }

  showInfo(message) {
    const alert = document.createElement('div')
    alert.className = 'alert alert-info mb-4'
    alert.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>${message}</span>
      <button class="btn btn-sm btn-ghost" onclick="this.parentElement.remove()">×</button>
    `
    this.element.insertBefore(alert, this.element.firstChild)

    // Auto-remove after 8 seconds
    setTimeout(() => alert.remove(), 8000)
  }

  showSuccess(message) {
    const alert = document.createElement('div')
    alert.className = 'alert alert-success mb-4'
    alert.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>${message}</span>
      <button class="btn btn-sm btn-ghost" onclick="this.parentElement.remove()">×</button>
    `
    this.element.insertBefore(alert, this.element.firstChild)

    // Auto-remove after 5 seconds
    setTimeout(() => alert.remove(), 5000)
  }

  setButtonLoading(loading) {
    if (!this.hasGenerateBtnTarget) return

    if (loading) {
      this.generateBtnTarget.disabled = true
      this.generateBtnTarget.innerHTML = `
        <span class="loading loading-spinner loading-sm"></span>
        Generating...
      `
    } else {
      this.generateBtnTarget.disabled = false
      this.generateBtnTarget.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        Generate Plan
      `
    }
  }

  getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
}
